import { Node, mergeAttributes, NodeViewProps } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";

// 👇 For use inside your React-based NodeView
const PlaceholderComponent = (props: NodeViewProps) => {
  const { node } = props;

  const token = node.attrs.token;
  const color = node.attrs.color;

  return (
    <NodeViewWrapper
      as="span"
      style={{
        backgroundColor: color,
        padding: "2px 4px",
        borderRadius: "4px",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
      data-placeholder
    >
      {token}
    </NodeViewWrapper>
  );
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    placeholder: {
      insertPlaceholder: (token: string, color: string) => ReturnType;
      deletePlaceholder: (token: string) => ReturnType;
    };
  }
}

export const Placeholder = Node.create({
  name: "placeholder",
  group: "inline",
  inline: true,
  atom: true, // 👈 makes it uneditable by default
  selectable: true,

  addAttributes() {
    return {
      token: { default: "" },
      color: { default: "#ccc" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-placeholder]", // Look for the span with data-placeholder
        getAttrs: (dom: HTMLElement) => ({
          token: dom.getAttribute("data-placeholder") ?? "", // Extract token from data-placeholder
          color: dom.getAttribute("color") ?? "#ccc", // Default color if not set
        }),
      },
    ];
  }
  ,

  renderHTML({ HTMLAttributes }) {
    return [
      "span", // Render as a span tag
      mergeAttributes(HTMLAttributes, {
        "data-placeholder": HTMLAttributes.token, // Pass token as data-placeholder
        color: HTMLAttributes.color, // Set color
        style: `background-color: ${HTMLAttributes.color}; padding: 2px 4px; border-radius: 4px; font-weight: 500; white-space: nowrap;`,
      }),
      HTMLAttributes.token, // Render the token text
    ];
  }
  
,

addCommands() {
  return {
    insertPlaceholder:
      (token: string, color: string) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { token, color },
          });
        },
    deletePlaceholder:
      (token: string) =>
        ({ state, tr, dispatch }) => {
          let removed = false;

          state.doc.descendants((node, pos) => {
            if (node.type.name === this.name && node.attrs.token === token) {
              tr.delete(pos, pos + node.nodeSize);
              removed = true;
            }
          });

          if (removed && dispatch) {
            dispatch(tr);
          }

          return removed;
        },
  };
}

  ,


  // 👇 Attach the custom React view
  addNodeView() {
    return ReactNodeViewRenderer(PlaceholderComponent);
  },
});
